import { z } from 'zod';
import { DomainAppKindSchema, DomainKindSchema, DomainStatusSchema } from './platform.js';
/**
 * Tenant hostnames (M11.5, M12.1, API.md §2.12 and §2.13, DATABASE.md §7.1
 * `tenant_domains`, DEPLOYMENT.md §10.2).
 *
 * Every tenant is reachable on `<slug>.<platform-domain>` the moment it exists. A
 * custom domain is the clinic pointing their own name at us, which takes two DNS
 * records and therefore has states:
 *
 * ```
 *   pending ──verify──► verified ──bind──► active
 *      │                    │
 *      └──verify fails──► failed ──verify──► (back to pending's checks)
 * ```
 *
 * `pending` and `failed` resolve to **nothing**: the host resolver only accepts
 * `verified` and `active`. That is the whole security property - anyone can point a
 * CNAME at our edge, so a row nobody has proven ownership of must not serve a clinic's
 * patients.
 */
/*
 * `DomainKindSchema`, `DomainStatusSchema` and `DomainAppKindSchema` are defined in
 * `platform.ts` (the super admin's tenant screens landed first) and imported rather
 * than restated. One definition per concept: a second copy of "what states may a
 * domain be in" is a second copy that can drift from the CHECK constraint.
 */
/**
 * Hostname rules, shared by the API and the admin form.
 *
 * Labels and dots only: no scheme, no port, no path, no trailing dot, no underscore.
 * The API normalizes to lower case before it stores or compares, because
 * `tenant_domains.host` is `citext` and a host is case-insensitive whatever a form
 * submitted.
 */
export const HOSTNAME_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;
export const HostnameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(4)
    .max(253)
    .regex(HOSTNAME_PATTERN, 'must be a hostname such as book.clinic.example');
/** The ownership record the clinic must publish. */
export const DomainTxtRecordSchema = z.object({
    /** `_ha-verify.<host>`. */
    name: z.string(),
    /** The token. Random, per domain row, and not a secret worth protecting - it proves */
    /* control of the DNS zone and nothing else. */
    value: z.string(),
});
export const TenantDomainDetailSchema = z.object({
    id: z.uuid(),
    host: z.string(),
    kind: DomainKindSchema,
    appKind: DomainAppKindSchema,
    status: DomainStatusSchema,
    /** The tenant's canonical host for this app kind. Never removable. */
    isPrimary: z.boolean(),
    /** What the `CNAME` must point at. Null for a platform subdomain. */
    cnameTarget: z.string().nullable(),
    /** Null for a platform subdomain, which needs no proof of ownership. */
    txtRecord: DomainTxtRecordSchema.nullable(),
    /** ISO 8601, or null while the records have not both been seen. */
    verifiedAt: z.string().nullable(),
    /** ISO 8601, set when the edge accepted the binding and the host began serving. */
    activatedAt: z.string().nullable(),
    /** ISO 8601 of the last DNS check, whatever its outcome. */
    lastCheckAt: z.string().nullable(),
    /** Why the last check failed, in words a clinic manager can act on. Never a stack. */
    lastError: z.string().nullable(),
});
export const AddDomainRequestSchema = z
    .object({
    host: HostnameSchema,
    appKind: DomainAppKindSchema,
})
    .strict();
export const DomainResponseSchema = z.object({ data: TenantDomainDetailSchema });
export const DomainListResponseSchema = z.object({ data: z.array(TenantDomainDetailSchema) });
/** The DNS label ownership tokens are published under (DEPLOYMENT.md §10.2). */
export const DOMAIN_VERIFICATION_PREFIX = '_ha-verify';
/** `_ha-verify.book.clinic.example` for `book.clinic.example`. */
export function verificationRecordName(host) {
    return `${DOMAIN_VERIFICATION_PREFIX}.${host.toLowerCase()}`;
}
//# sourceMappingURL=domains.js.map